import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {TableSorter} from '../../../../../tools/table-sorter/table-sorter';
import { DatePipe } from '@angular/common';
import {CustomPayout} from '../../../../../models/custom-payout';
import {CustomPayoutService} from '../../../../../services/custom-payout/custom-payout.service';
import {RouterLink} from '@angular/router';
import {IsGrantedDirective} from '../../../../../directives/is-granted/is-granted.directive';
import {IsGranted} from '../../../../../decorator/is-granted';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {PartnerTabComponent} from '../../partner-tab/partner-tab.component';
import {Partner} from '../../../../../models/partner/partner';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {UserEventModalComponent} from '../../../../layout/user-event-modal/user-event-modal.component';
import {forkJoin, Observable} from 'rxjs';
import {AuthService} from '../../../../../services/auth/auth.service';
import {CurrencyPipe} from '../../../../../pipes/currency/currency.pipe';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';

@Component({
  selector: 'app-custom-payout-list',
  imports: [DatePipe, RouterLink, IsGrantedDirective, CurrencyPipe, ConfirmDirective],
  templateUrl: './custom-payout-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './custom-payout-list.component.scss'
})
@IsGranted(['ROLE_API_CUSTOM_PAYOUT_READ'])
@MetaTitle('Custom payouts')
export class CustomPayoutListComponent implements OnInit {

  partner: Partner | null = null;
  customPayouts: CustomPayout[] = [];
  tableSorter: TableSorter = new TableSorter('position', 'asc');

  constructor(
    protected loader: LoaderService,
    private snackbar: SnackbarService,
    private customPayoutService: CustomPayoutService,
    private dialog: DialogService,
    private auth: AuthService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.partnerTab.partnerSubject.subscribe((partner: Partner | null) => {
      if (partner) {
        this.partner = partner;
        this.fetchAll();
      }
    });
  }

  delete(customPayout: CustomPayout): void {
    if (customPayout.id) {
      this.customPayoutService.delete(customPayout.id).subscribe(() => {
        this.fetchAll();
        this.snackbar.success('The custom payout rule has been deleted successfully.');
      });
    }
  }

  openUserEventModal(customPayout: CustomPayout): void {
    this.dialog.openDialog(UserEventModalComponent, {
      data: {
        title: 'Custom Payout Logs',
        key: 'custom-payout-' + customPayout.id
      },
      backdrop: false
    }).subscribe(() => {
    });
  }

  fetchAll(): void {
    this.loader.startLoading();

    const parameters: any = {};
    if (this.partner && this.partner.id) {
      parameters.partnerId = this.partner.id;
    }

    this.customPayoutService.findAll(parameters, null, this.tableSorter).subscribe((customPayouts: CustomPayout[]) => {
      this.customPayouts = customPayouts;

      this.loader.stopLoading();
    });
  }

  moveRule(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= this.customPayouts.length) {
      return;
    }

    this.loader.startLoading();

    [this.customPayouts[index].position, this.customPayouts[newIndex].position] =
      [this.customPayouts[newIndex].position, this.customPayouts[index].position];

    const requests: Observable<void>[] = [];

    const current = this.customPayouts[index];
    if (current.id) {
      requests.push(this.customPayoutService.update(current.id, {position: current.position}));
    }

    const next = this.customPayouts[newIndex];
    if (next.id) {
      requests.push(this.customPayoutService.update(next.id, {position: next.position}));
    }

    forkJoin(requests).subscribe(() => {
      this.fetchAll();
    });
  }

  getCurrency(): string | null {
    return this.auth.getConfig()?.currency ?? null;
  }
}
